import React from 'react';
import { Add as AddIcon, CloudUpload as PushIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, Button, Chip, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../../utils/configVocabulary';

type PoliciesIntroSectionProps = {
  qReturnTo: string;
  canGoBack: boolean;
  onBack: () => void;
  canWritePolicies: boolean;
  canDeployPolicies: boolean;
  onOpenAddPolicy: () => void;
  onOpenAddPolicyPack: () => void;
  onDeployAll: () => void;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>) => void;
  packSummaries: Array<{
    key: string;
    label: string;
    valueProp: string;
    count: number;
    activeCount: number;
    inactiveCount: number;
  }>;
  selectedPack: string;
  onSelectPack: (pack: any) => void;
  summary: {
    total: number;
    active: number;
    inactive: number;
    needsZone: number;
    pendingDeploy: number;
    packCount: number;
  };
};

const PoliciesIntroSection: React.FC<PoliciesIntroSectionProps> = ({
  qReturnTo,
  canGoBack,
  onBack,
  canWritePolicies,
  canDeployPolicies,
  onOpenAddPolicy,
  onOpenAddPolicyPack,
  onDeployAll,
  onOpenMenu,
  packSummaries,
  selectedPack,
  onSelectPack,
  summary,
}) => (
  <>
    <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3, borderColor: '#e5e7eb' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
            Configure
          </Typography>
          <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
            Safety Packs & Policies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure which behaviors are monitored, where they run, and what still needs deployment across packs, {CONFIG_SCOPE_LABELS.edgeDevice.toLowerCase()}s, and {CONFIG_SCOPE_LABELS.cameraStream.toLowerCase()}s.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            In the safety workflow, a policy defines the safety condition being scanned in a selected camera view and zone.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip label={`${summary.total} policies`} color="primary" />
            <Chip label={`${summary.active} ${CONFIG_STATE_LABELS.live.toLowerCase()}`} variant="outlined" />
            <Chip label={`${summary.inactive} ${CONFIG_STATE_LABELS.draft.toLowerCase()}`} variant="outlined" />
            <Chip
              label={`${summary.needsZone} ${CONFIG_SCOPE_LABELS.zone.toLowerCase()} scope gaps`}
              color={summary.needsZone > 0 ? 'warning' : 'success'}
              variant={summary.needsZone > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              label={`${summary.pendingDeploy} ${CONFIG_STATE_LABELS.needsAction.toLowerCase()}`}
              color={summary.pendingDeploy > 0 ? 'warning' : 'success'}
              variant={summary.pendingDeploy > 0 ? 'filled' : 'outlined'}
            />
            <Chip label={`${summary.packCount} packs`} variant="outlined" />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canGoBack && (
            <Button variant="outlined" onClick={onBack}>
              {qReturnTo === 'setup' ? 'Back To Setup Policies' : 'Back'}
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenAddPolicy} disabled={!canWritePolicies}>
            Add Policy
          </Button>
          <Button variant="contained" color="success" startIcon={<PushIcon />} onClick={onDeployAll} disabled={!canDeployPolicies}>
            Deploy Changes
          </Button>
          <Button variant="outlined" onClick={onOpenAddPolicyPack} disabled={!canWritePolicies}>
            Add Policy Pack
          </Button>
          <IconButton onClick={onOpenMenu} aria-label="More policy actions">
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Box>
    </Paper>

    <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} variant="outlined">
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Pack filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter the policy list by pack without leaving the management table.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button size="small" variant={selectedPack === 'all' ? 'contained' : 'outlined'} onClick={() => onSelectPack('all')}>
            All Packs
          </Button>
          {packSummaries.map((pack) => (
            <Button
              key={pack.key}
              size="small"
              variant={selectedPack === pack.key ? 'contained' : 'outlined'}
              onClick={() => onSelectPack(pack.key)}
            >
              {pack.label} ({pack.count})
            </Button>
          ))}
        </Stack>
        <Grid container spacing={1.5}>
          {packSummaries.map((pack) => (
            <Grid item xs={12} md={4} key={pack.key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: selectedPack === pack.key ? 'primary.main' : 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {pack.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {pack.valueProp}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {pack.count} policies · {pack.activeCount} {CONFIG_STATE_LABELS.live.toLowerCase()} · {pack.inactiveCount} {CONFIG_STATE_LABELS.draft.toLowerCase()}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Paper>
  </>
);

export default PoliciesIntroSection;
